import React, { useMemo, useState, useEffect } from "react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import { Box, Typography, CircularProgress, Grid, FormControlLabel, Checkbox, FormGroup } from "@mui/material";
import { format, differenceInDays, eachDayOfInterval } from "date-fns";

const MultiLineMetricsChart = ({
    leadData = [],
    analyticsData = null,
    dateRange,
    loading = false,
}) => {
    console.log('🚀 MultiLineMetricsChart COMPONENT CALLED with:', {
        hasAnalyticsData: !!analyticsData,
        analyticsDataType: typeof analyticsData,
        hasTimeline: !!analyticsData?.timeline,
        dateRange,
        loading,
        leadDataLength: leadData.length
    });
    
    // Temporary alert to check if component is called
    if (analyticsData && analyticsData.timeline) {
        console.log('🎯 CHART COMPONENT HAS ANALYTICS DATA WITH TIMELINE!');
    }

    // State for toggling line visibility
    const [visibleLines, setVisibleLines] = useState({
        'Page Views': true,
        'Drop-offs': true,
        'Online Estimates': true,
        'Price Estimates': true,
        'Onsite Bookings': true
    });

    // Additional state to track debugging info
    const [debugInfo, setDebugInfo] = useState({
        pageViewsCount: 0,
        datesWithPageViews: 0,
        dataLoaded: false
    });

    // Effect to collect and display debug info for analytics data
    useEffect(() => {
        if (!analyticsData?.timeline) return;

        try {
            const timelinePageViews = analyticsData.timeline.page_views || [];
            const totalPageViews = timelinePageViews.reduce((sum, day) => sum + (day.page_views || 0), 0);

            setDebugInfo({
                pageViewsCount: totalPageViews,
                datesWithPageViews: timelinePageViews.length,
                dataLoaded: true
            });

            console.log('Analytics Timeline Debug:', {
                totalPageViews,
                timelineDays: timelinePageViews.length,
                timelineData: timelinePageViews
            });
        } catch (e) {
            console.error('Error in analytics debug collection:', e);
        }
    }, [analyticsData]);

    const { chartData, pieData } = useMemo(() => {
        console.log('=== ANALYTICS TIMELINE PROCESSING START ===');
        console.log('Analytics data:', analyticsData);
        console.log('Timeline data available:', !!analyticsData?.timeline);
        console.log('Date range:', dateRange);

        // Early validation for analytics data
        if (!analyticsData || !analyticsData.timeline) {
            console.warn('❌ No analytics timeline data available');
            return { chartData: [], pieData: [] };
        }

        const { timeline } = analyticsData;
        const timelinePageViews = timeline.page_views || [];
        const timelineLeads = timeline.leads || [];

        if (timelinePageViews.length === 0 && timelineLeads.length === 0) {
            console.warn('❌ No timeline data in analytics response');
            return { chartData: [], pieData: [] };
        }

        console.log('✅ Timeline data found:', {
            pageViewDays: timelinePageViews.length,
            leadDays: timelineLeads.length
        });

        if (timelinePageViews.length > 0) {
            console.log('✅ First page view item:', timelinePageViews[0]);
            console.log('✅ Available fields in first item:', Object.keys(timelinePageViews[0]));
        } else {
            console.warn('❌ No page view timeline data available');
        }

        if (timelinePageViews.length === 0 && timelineLeads.length === 0) {
            console.warn('❌ No timeline data available at all');
            return { chartData: [], pieData: [] };
        }

        // Determine the date range
        let startDate, endDate;

        if (dateRange?.start && dateRange?.end) {
            startDate = new Date(dateRange.start);
            endDate = new Date(dateRange.end);
            console.log('📅 Using provided date range:', {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                startValid: !isNaN(startDate.getTime()),
                endValid: !isNaN(endDate.getTime())
            });
        } else {
            console.log('📅 No date range provided, calculating from timeline data...');
            
            // Find min/max dates from timeline data
            const allTimelineDates = [
                ...timelinePageViews.map(item => new Date(item.date)),
                ...timelineLeads.map(item => new Date(item.date))
            ].filter(date => !isNaN(date.getTime()));
                
            console.log('📅 Valid timeline dates found:', allTimelineDates.length);
            if (allTimelineDates.length > 0) {
                console.log('📅 Timeline date range:', {
                    earliest: new Date(Math.min(...allTimelineDates)).toISOString(),
                    latest: new Date(Math.max(...allTimelineDates)).toISOString()
                });
            }

            if (allTimelineDates.length === 0) {
                console.warn('❌ No valid dates found in timeline data');
                return { chartData: [], pieData: [] };
            }

            startDate = new Date(Math.min(...allTimelineDates.map(d => d.getTime())));
            endDate = new Date(Math.max(...allTimelineDates.map(d => d.getTime())));
            
            console.log('📅 Calculated date range from timeline:', {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                totalDays: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
            });
        }

        // Validate dates
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return { chartData: [], pieData: [] };
        }

        // Group page views by date from analytics timeline
        const pageViewsByDate = {};
        timelinePageViews.forEach(item => {
            try {
                if (!item || !item.date) {
                    console.warn('Invalid timeline page view item:', item);
                    return;
                }

                const date = new Date(item.date);
                if (isNaN(date.getTime())) {
                    console.warn('Invalid date in timeline page view:', item.date);
                    return;
                }

                // Check if date is within range BEFORE formatting
                // Use date strings for comparison to avoid timezone issues
                if (dateRange?.start && dateRange?.end) {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    if (dateStr < dateRange.start || dateStr > dateRange.end) {
                        console.log(`🚫 Filtering out page view - date ${dateStr} is outside range ${dateRange.start} to ${dateRange.end}`);
                        return;
                    }
                }

                const dateStr = format(date, 'yyyy-MM-dd');
                pageViewsByDate[dateStr] = item.page_views || 0;

                // Debug: log when we successfully process a page view
                console.log(`✅ Added page views for date ${dateStr}: ${item.page_views}`);

            } catch (e) {
                console.error("Error processing timeline page view date:", e, item);
            }
        });

        // Debug: show the result of page views grouping
        console.log('📊 Page views grouped by date:', pageViewsByDate);
        console.log('📊 Total days with page views:', Object.keys(pageViewsByDate).length);
        console.log('📊 Total page view count:', Object.values(pageViewsByDate).reduce((sum, count) => sum + count, 0));

        // Group leads by date from analytics timeline
        const leadsByDate = {};
        const leadTypeCounts = {
            'Drop-offs': 0,
            'Online Estimates': 0,
            'Price Estimates': 0,
            'Onsite Bookings': 0,
        };

        timelineLeads.forEach(item => {
            try {
                if (!item || !item.date) {
                    console.warn('Invalid timeline lead item:', item);
                    return;
                }

                const leadDate = new Date(item.date);
                if (isNaN(leadDate.getTime())) {
                    console.warn('Invalid date in timeline lead:', item.date);
                    return;
                }

                const dateStr = format(leadDate, 'yyyy-MM-dd');
                if (!leadsByDate[dateStr]) {
                    leadsByDate[dateStr] = {
                        dropoffs: 0,
                        onlineEstimates: 0,
                        priceEstimates: 0,
                        onsiteBookings: 0,
                    };
                }

                // Use timeline data values directly
                leadsByDate[dateStr].dropoffs = item.dropoffs || 0;
                leadsByDate[dateStr].onlineEstimates = item.online_estimates || 0;
                leadsByDate[dateStr].priceEstimates = item.price_estimates || 0;
                leadsByDate[dateStr].onsiteBookings = item.onsite_bookings || 0;

                // Update totals
                leadTypeCounts['Drop-offs'] += item.dropoffs || 0;
                leadTypeCounts['Online Estimates'] += item.online_estimates || 0;
                leadTypeCounts['Price Estimates'] += item.price_estimates || 0;
                leadTypeCounts['Onsite Bookings'] += item.onsite_bookings || 0;

            } catch (e) {
                console.error("Error processing timeline lead data:", e, item);
            }
        });

        // Create data arrays for all metrics
        let datePoints = [];
        try {
            const dayDiff = differenceInDays(endDate, startDate);
            if (dayDiff > 30) {
                // For large date ranges, sample every few days
                const sampleInterval = Math.ceil(dayDiff / 30);
                let current = new Date(startDate);
                while (current <= endDate) {
                    const dateStr = format(current, 'yyyy-MM-dd');
                    datePoints.push(dateStr);
                    current = new Date(current.setDate(current.getDate() + sampleInterval));
                }
            } else {
                // For smaller ranges, use all days
                eachDayOfInterval({ start: startDate, end: endDate }).forEach(date => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    datePoints.push(dateStr);
                });
            }
        } catch (e) {
            console.error("Error creating date points:", e);
            return { chartData: [], pieData: [] };
        }

        // Build chart data for all metrics
        const allChartData = [
            {
                id: 'Page Views',
                color: '#4fc3f7',
                data: datePoints.map(dateStr => {
                    const value = pageViewsByDate[dateStr] || 0;
                    if (value > 0) {
                        console.log(`📊 Chart point: ${dateStr} has ${value} page views`);
                    }
                    return {
                        x: dateStr,
                        y: value
                    };
                })
            },
            {
                id: 'Drop-offs',
                color: '#ef5350',
                data: datePoints.map(dateStr => ({
                    x: dateStr,
                    y: leadsByDate[dateStr]?.dropoffs || 0
                }))
            },
            {
                id: 'Online Estimates',
                color: '#9575cd',
                data: datePoints.map(dateStr => ({
                    x: dateStr,
                    y: leadsByDate[dateStr]?.onlineEstimates || 0
                }))
            },
            {
                id: 'Price Estimates',
                color: '#ba68c8',
                data: datePoints.map(dateStr => ({
                    x: dateStr,
                    y: leadsByDate[dateStr]?.priceEstimates || 0
                }))
            },
            {
                id: 'Onsite Bookings',
                color: '#ff9800',
                data: datePoints.map(dateStr => ({
                    x: dateStr,
                    y: leadsByDate[dateStr]?.onsiteBookings || 0
                }))
            }
        ];

        // Debug: analyze the final chart data
        console.log('📈 Final chart data analysis:');
        allChartData.forEach(series => {
            const nonZeroPoints = series.data.filter(point => point.y > 0);
            console.log(`  ${series.id}: ${nonZeroPoints.length} non-zero points out of ${series.data.length} total`);
            if (nonZeroPoints.length > 0) {
                console.log(`    Sample non-zero values:`, nonZeroPoints.slice(0, 3).map(p => `${p.x}: ${p.y}`));
            }
            if (series.id === 'Page Views') {
                console.log(`    Page Views total value:`, series.data.reduce((sum, p) => sum + p.y, 0));
                console.log(`    Date range covered:`, {
                    first: series.data[0]?.x,
                    last: series.data[series.data.length - 1]?.x,
                    totalPoints: series.data.length
                });
            }
        });

        // Create pie chart data
        const pieChartData = Object.entries(leadTypeCounts)
            .filter(([_, count]) => count > 0)
            .map(([type, value]) => {
                let color;
                switch (type) {
                    case 'Drop-offs':
                        color = '#ef5350';
                        break;
                    case 'Onsite Bookings':
                        color = '#ff9800';
                        break;
                    case 'Online Estimates':
                        color = '#9575cd';
                        break;
                    case 'Price Estimates':
                        color = '#ba68c8';
                        break;
                    default:
                        color = '#81c784';
                }

                return {
                    id: type,
                    label: type,
                    value,
                    color
                };
            });

        console.log('🔍 FINAL CHART DATA RETURN:', {
            chartDataLength: allChartData.length,
            pieDataLength: pieChartData.length,
            allChartData: allChartData.map(series => ({
                id: series.id,
                dataPoints: series.data.length,
                totalValue: series.data.reduce((sum, p) => sum + p.y, 0)
            }))
        });

        return {
            chartData: allChartData,
            pieData: pieChartData
        };
    }, [leadData, analyticsData, dateRange]);

    const handleLineToggle = (lineId) => {
        setVisibleLines(prev => ({
            ...prev,
            [lineId]: !prev[lineId]
        }));
    };

    // Filter chart data based on visible lines
    const filteredChartData = chartData.filter(line => visibleLines[line.id]);
    
    console.log('🔍 FILTERING CHART DATA:', {
        originalChartDataLength: chartData.length,
        visibleLines,
        filteredChartDataLength: filteredChartData.length,
        originalChartDataIds: chartData.map(d => d.id),
        filteredChartDataIds: filteredChartData.map(d => d.id)
    });

    // Log the first few data points for each series to help with debugging
    useEffect(() => {
        if (filteredChartData && filteredChartData.length > 0) {
            console.log('Chart data preview:');
            filteredChartData.forEach(series => {
                const hasNonZeroValues = series.data.some(point => point.y > 0);
                console.log(`Series '${series.id}': ${series.data.length} points, has non-zero values: ${hasNonZeroValues}`);
                if (series.data.length > 0) {
                    console.log(`  First few points: `, series.data.slice(0, 3));

                    // For page views specifically, show more details
                    if (series.id === 'Page Views') {
                        const nonZeroPoints = series.data.filter(point => point.y > 0);
                        console.log(`  Page Views with values > 0: ${nonZeroPoints.length} of ${series.data.length}`);
                        if (nonZeroPoints.length > 0) {
                            console.log(`  Sample non-zero points:`, nonZeroPoints.slice(0, 3));
                        }
                    }
                }
            });
        }
    }, [filteredChartData]);

    if (loading) {
        return (
            <Box sx={{
                width: '100%',
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start', // Changed from center to left-aligned
                bgcolor: '#1a1a1a',
                borderRadius: 2,
                my: 3,
                pl: 3 // Add left padding for better spacing
            }}>
                <CircularProgress sx={{ color: '#4fc3f7' }} />
            </Box>
        );
    }

    if (!filteredChartData.length) {
        console.log('❌ NO FILTERED CHART DATA - filteredChartData.length is:', filteredChartData.length);
        console.log('❌ Raw chartData.length:', chartData.length);
        console.log('❌ visibleLines:', visibleLines);
        console.log('❌ analyticsData exists:', !!analyticsData);
        console.log('❌ timeline exists:', !!analyticsData?.timeline);
        console.log('❌ page_views exists:', !!analyticsData?.timeline?.page_views);
        console.log('❌ page_views length:', analyticsData?.timeline?.page_views?.length || 0);
        console.log('❌ dateRange:', dateRange);
        return (
            <Box sx={{
                width: '100%',
                height: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1a1a1a',
                borderRadius: 2,
                my: 3,
                p: 3
            }}>
                <Typography variant="body1" sx={{ color: '#9e9e9e', mb: 2 }}>
                    No data available for the selected date range
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', textAlign: 'center', mb: 1 }}>
                    Chart Data: {chartData.length} series | Filtered: {filteredChartData.length} series
                </Typography>

                {/* Enhanced debug information */}
                {debugInfo.dataLoaded && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        borderRadius: 1,
                        border: '1px solid rgba(255,255,255,0.1)',
                        maxWidth: '80%'
                    }}>
                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', mb: 0.5 }}>
                            <strong>Debug Info:</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', ml: 1 }}>
                            • Valid page views: {debugInfo.pageViewsCount}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', ml: 1 }}>
                            • Unique dates with views: {debugInfo.datesWithPageViews}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', ml: 1 }}>
                            • Selected date range: {dateRange?.start && dateRange?.end ?
                                `${dateRange.start} to ${dateRange.end}` :
                                'No date range specified'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', ml: 1 }}>
                            • Visible lines: {Object.entries(visibleLines).filter(([_, visible]) => visible).map(([line, _]) => line).join(', ') || 'None'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', ml: 1 }}>
                            • Chart series available: {chartData.map(d => d.id).join(', ') || 'None'}
                        </Typography>
                    </Box>
                )}

                {analyticsData?.timeline?.page_views && analyticsData.timeline.page_views.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#4fc3f7', textAlign: 'center', mt: 2 }}>
                        Page view data exists but may be outside the selected date range
                    </Typography>
                )}
            </Box>
        );
    }

    const hasPieData = pieData && pieData.length > 0;

    return (
        <Box sx={{
            width: '100%',
            mt: 3,
            mb: 5,
            bgcolor: '#1a1a1a',
            borderRadius: 2,
            border: '1px solid #333',
            overflow: 'visible',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            p: 2,
            mx: 0 // Ensure no auto margins
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                    Multi-Metric Analytics Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                    {dateRange?.start && dateRange?.end ?
                        `${format(new Date(dateRange.start), 'MMM d, yyyy')} - ${format(new Date(dateRange.end), 'MMM d, yyyy')}` :
                        'All Time'}
                </Typography>
            </Box>

            {/* Legend with checkboxes for toggling lines */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'flex-start', // Changed from center to left-aligned
                mb: 3,
                bgcolor: 'rgba(0,0,0,0.3)',
                borderRadius: 1,
                p: 2,
                border: '1px solid rgba(255,255,255,0.1)'
            }}>
                <FormGroup row>
                    {chartData.map((line) => (
                        <FormControlLabel
                            key={line.id}
                            control={
                                <Checkbox
                                    checked={visibleLines[line.id]}
                                    onChange={() => handleLineToggle(line.id)}
                                    sx={{
                                        color: line.color,
                                        '&.Mui-checked': {
                                            color: line.color,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 20,
                                        }
                                    }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: line.color,
                                            border: '1px solid white'
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 'medium', fontSize: 12 }}>
                                        {line.id}
                                    </Typography>
                                </Box>
                            }
                            sx={{ mr: 2 }}
                        />
                    ))}
                </FormGroup>
            </Box>

            {/* Main content with multi-line chart and pie chart */}
            <Box sx={{
                width: '100%',
                overflowX: 'auto', // Allow horizontal scrolling if needed on very small screens
                minWidth: hasPieData ? '800px' : 'auto' // Increased minimum width
            }}>
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    width: '100%',
                    minWidth: hasPieData ? '800px' : 'auto',
                    alignItems: 'stretch'
                }}>
                    {/* Multi-line chart */}
                    <Box sx={{
                        flex: hasPieData ? '1' : '1',
                        minWidth: hasPieData ? '400px' : 'auto',
                        width: hasPieData ? '50%' : '100%'
                    }}>
                        <Box sx={{
                            height: { xs: 300, sm: 350, md: 400 }, // Responsive height
                            width: '100%',
                            minHeight: '250px' // Minimum height for usability
                        }}>
                            <ResponsiveLine
                                data={filteredChartData}
                                margin={{ top: 20, right: 30, bottom: 40, left: 50 }}
                                xScale={{
                                    type: 'point',
                                    padding: 0.5,
                                }}
                                yScale={{
                                    type: 'linear',
                                    min: 0,
                                    max: 'auto',
                                    stacked: false,
                                }}
                                curve="monotoneX"
                                enableArea={true}
                                areaOpacity={0.3}
                                enablePoints={true}
                                pointSize={6}
                                pointColor={{ from: 'serieColor' }}
                                pointBorderWidth={2}
                                pointBorderColor={{ from: 'serieColor' }}
                                useMesh={true}
                                enableSlices="x"
                                colors={{ datum: 'color' }}
                                lineWidth={3}
                                defs={[
                                    // Page Views gradient (light blue)
                                    {
                                        id: 'pageViewsGradient',
                                        type: 'linearGradient',
                                        colors: [
                                            { offset: 0, color: '#4fc3f7', opacity: 0.8 },
                                            { offset: 100, color: '#4fc3f7', opacity: 0.1 }
                                        ],
                                    },
                                    // Drop-offs gradient (red)
                                    {
                                        id: 'dropoffsGradient',
                                        type: 'linearGradient',
                                        colors: [
                                            { offset: 0, color: '#ef5350', opacity: 0.8 },
                                            { offset: 100, color: '#ef5350', opacity: 0.1 }
                                        ],
                                    },
                                    // Online Estimates gradient (purple)
                                    {
                                        id: 'onlineEstimatesGradient',
                                        type: 'linearGradient',
                                        colors: [
                                            { offset: 0, color: '#9575cd', opacity: 0.8 },
                                            { offset: 100, color: '#9575cd', opacity: 0.1 }
                                        ],
                                    },
                                    // Price Estimates gradient (magenta)
                                    {
                                        id: 'priceEstimatesGradient',
                                        type: 'linearGradient',
                                        colors: [
                                            { offset: 0, color: '#ba68c8', opacity: 0.8 },
                                            { offset: 100, color: '#ba68c8', opacity: 0.1 }
                                        ],
                                    },
                                    // Onsite Bookings gradient (orange)
                                    {
                                        id: 'onsiteBookingsGradient',
                                        type: 'linearGradient',
                                        colors: [
                                            { offset: 0, color: '#ff9800', opacity: 0.8 },
                                            { offset: 100, color: '#ff9800', opacity: 0.1 }
                                        ],
                                    }
                                ]}
                                fill={[
                                    { match: { id: 'Page Views' }, id: 'pageViewsGradient' },
                                    { match: { id: 'Drop-offs' }, id: 'dropoffsGradient' },
                                    { match: { id: 'Online Estimates' }, id: 'onlineEstimatesGradient' },
                                    { match: { id: 'Price Estimates' }, id: 'priceEstimatesGradient' },
                                    { match: { id: 'Onsite Bookings' }, id: 'onsiteBookingsGradient' }
                                ]}
                                sliceTooltip={({ slice }) => {
                                    if (!slice?.points?.length) return null;

                                    try {
                                        const date = slice.points[0].data.x;

                                        return (
                                            <Box sx={{
                                                bgcolor: '#222222',
                                                color: '#ffffff',
                                                p: 1.5,
                                                borderRadius: 1,
                                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                                                border: '1px solid #444',
                                                maxWidth: 250
                                            }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {format(new Date(date), 'MMM d, yyyy')}
                                                </Typography>
                                                {slice.points.map((point, i) => (
                                                    <Typography key={i} variant="body2" sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: point.serieColor,
                                                        fontWeight: 'bold',
                                                        mb: 0.5
                                                    }}>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            backgroundColor: point.serieColor || point.color,
                                                            border: `1px solid ${point.serieColor || point.color}`,
                                                            boxShadow: `0 0 4px ${point.serieColor || point.color}40`
                                                        }}></span>
                                                        {point.serieId}: {point.data.y}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        );
                                    } catch (e) {
                                        console.error("Error creating tooltip:", e);
                                        return null;
                                    }
                                }}
                                crosshairType="cross"
                                enableGridX={false}
                                gridYValues={5}
                                theme={{
                                    background: 'transparent',
                                    textColor: '#ffffff',
                                    fontSize: 11,
                                    axis: {
                                        domain: {
                                            line: {
                                                stroke: '#555555',
                                                strokeWidth: 1
                                            }
                                        },
                                        ticks: {
                                            line: {
                                                stroke: '#555555',
                                                strokeWidth: 1
                                            },
                                            text: {
                                                fill: '#ffffff',
                                                fontSize: 10
                                            }
                                        },
                                        legend: {
                                            text: {
                                                fill: '#ffffff',
                                                fontSize: 10
                                            }
                                        }
                                    },
                                    grid: {
                                        line: {
                                            stroke: '#444444',
                                            strokeWidth: 1
                                        }
                                    },
                                    tooltip: {
                                        container: {
                                            background: '#222222',
                                            color: '#ffffff',
                                            fontSize: 12,
                                            borderRadius: 4,
                                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
                                        }
                                    },
                                    crosshair: {
                                        line: {
                                            stroke: '#ffffff',
                                            strokeWidth: 1,
                                            strokeOpacity: 0.35
                                        }
                                    }
                                }}
                                axisBottom={{
                                    orient: 'bottom',
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: -45,
                                    legend: 'Date',
                                    legendOffset: 45,
                                    legendPosition: 'middle',
                                    format: value => {
                                        try {
                                            const date = new Date(value);
                                            return isNaN(date.getTime()) ? value : format(date, 'MMM d');
                                        } catch (e) {
                                            return value;
                                        }
                                    },
                                    // Limit the number of ticks for better readability
                                    tickValues: filteredChartData[0]?.data.length > 10
                                        ? filteredChartData[0].data.filter((_, i) => i % Math.ceil(filteredChartData[0].data.length / 10) === 0).map(d => d.x)
                                        : undefined
                                }}
                                axisLeft={{
                                    orient: 'left',
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Count',
                                    legendOffset: -45,
                                    legendPosition: 'middle'
                                }}
                                isInteractive={true}
                            />
                        </Box>
                    </Box>

                    {/* Pie chart */}
                    {hasPieData && (
                        <Box sx={{
                            flex: '1',
                            minWidth: '400px',
                            width: '50%'
                        }}>
                            <Box sx={{
                                height: { xs: 300, sm: 350, md: 400 }, // Responsive height matching line chart
                                width: '100%',
                                minHeight: '250px', // Minimum height for usability
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                position: 'relative',
                                px: 0,
                                mx: 'auto',
                                overflow: 'visible'
                            }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        color: '#ffffff',
                                        mb: 1,
                                        textAlign: 'center',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Lead Type Distribution
                                </Typography>

                                <Box sx={{
                                    height: { xs: 250, sm: 300, md: 360 }, // Responsive height for pie chart content
                                    position: 'relative',
                                    overflow: 'visible'
                                }}>
                                    <ResponsivePie
                                        data={pieData && pieData.length > 0 ? pieData : [{ id: "No Data", label: "No Data", value: 1 }]}
                                        margin={{
                                            top: 20,
                                            right: 80,
                                            bottom: 20,
                                            left: 80
                                        }}
                                        innerRadius={0.4}
                                        padAngle={1.2}
                                        cornerRadius={6}
                                        activeOuterRadiusOffset={12}
                                        enableArcLabels={false}
                                        colors={d => {
                                            if (d.data && d.data.color) return d.data.color;

                                            switch (d.id) {
                                                case 'Drop-offs': return '#ef5350';
                                                case 'Onsite Bookings': return '#ff9800';
                                                case 'Online Estimates': return '#9575cd';
                                                case 'Price Estimates': return '#ba68c8';
                                                default: return '#666666';
                                            }
                                        }}
                                        borderWidth={2}
                                        borderColor="#000000"
                                        arcLinkLabelsSkipAngle={10}
                                        arcLinkLabelsTextColor="#ffffff"
                                        arcLinkLabelsThickness={2}
                                        arcLinkLabelsColor={{ from: 'color' }}
                                        arcLinkLabelsDiagonalLength={25}
                                        arcLinkLabelsStraightLength={25}
                                        arcLinkLabelsOffset={6}
                                        arcLinkLabelsFontSize={10}
                                        defs={[
                                            // Glossy gradient for Drop-offs
                                            {
                                                id: 'dropoffsGloss',
                                                type: 'linearGradient',
                                                colors: [
                                                    { offset: 0, color: '#ff6b6b', opacity: 1 },
                                                    { offset: 30, color: '#ef5350', opacity: 1 },
                                                    { offset: 70, color: '#e53935', opacity: 1 },
                                                    { offset: 100, color: '#c62828', opacity: 1 }
                                                ],
                                            },
                                            // Glossy gradient for Onsite Bookings
                                            {
                                                id: 'onsiteGloss',
                                                type: 'linearGradient',
                                                colors: [
                                                    { offset: 0, color: '#ffb74d', opacity: 1 },
                                                    { offset: 30, color: '#ff9800', opacity: 1 },
                                                    { offset: 70, color: '#f57c00', opacity: 1 },
                                                    { offset: 100, color: '#e65100', opacity: 1 }
                                                ],
                                            },
                                            // Glossy gradient for Online Estimates
                                            {
                                                id: 'onlineGloss',
                                                type: 'linearGradient',
                                                colors: [
                                                    { offset: 0, color: '#b39ddb', opacity: 1 },
                                                    { offset: 30, color: '#9575cd', opacity: 1 },
                                                    { offset: 70, color: '#7e57c2', opacity: 1 },
                                                    { offset: 100, color: '#512da8', opacity: 1 }
                                                ],
                                            },
                                            // Glossy gradient for Price Estimates
                                            {
                                                id: 'priceGloss',
                                                type: 'linearGradient',
                                                colors: [
                                                    { offset: 0, color: '#ce93d8', opacity: 1 },
                                                    { offset: 30, color: '#ba68c8', opacity: 1 },
                                                    { offset: 70, color: '#ab47bc', opacity: 1 },
                                                    { offset: 100, color: '#7b1fa2', opacity: 1 }
                                                ],
                                            },
                                            // Glossy gradient for No Data
                                            {
                                                id: 'noDataGloss',
                                                type: 'linearGradient',
                                                colors: [
                                                    { offset: 0, color: '#757575', opacity: 1 },
                                                    { offset: 30, color: '#666666', opacity: 1 },
                                                    { offset: 70, color: '#424242', opacity: 1 },
                                                    { offset: 100, color: '#212121', opacity: 1 }
                                                ],
                                            }
                                        ]}
                                        fill={[
                                            { match: { id: 'Drop-offs' }, id: 'dropoffsGloss' },
                                            { match: { id: 'Onsite Bookings' }, id: 'onsiteGloss' },
                                            { match: { id: 'Online Estimates' }, id: 'onlineGloss' },
                                            { match: { id: 'Price Estimates' }, id: 'priceGloss' },
                                            { match: { id: 'No Data' }, id: 'noDataGloss' }
                                        ]}
                                        theme={{
                                            background: 'transparent',
                                            textColor: '#ffffff',
                                            fontSize: 11,
                                            tooltip: {
                                                container: {
                                                    background: '#222222',
                                                    color: '#ffffff',
                                                    fontSize: 12,
                                                    borderRadius: 4,
                                                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
                                                }
                                            },
                                            svg: {
                                                overflow: 'visible'
                                            }
                                        }}
                                        tooltip={({ datum }) => (
                                            <div style={{
                                                padding: '12px 16px',
                                                background: 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                backdropFilter: 'blur(8px)'
                                            }}>
                                                <div
                                                    style={{
                                                        width: '14px',
                                                        height: '14px',
                                                        background: `linear-gradient(135deg, ${datum.color || datum.data.color}dd 0%, ${datum.color || datum.data.color} 100%)`,
                                                        borderRadius: '50%',
                                                        marginRight: '10px',
                                                        boxShadow: `0 2px 8px ${datum.color || datum.data.color}40, inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
                                                        border: '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}
                                                />
                                                <div>
                                                    <strong style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}>
                                                        {datum.label || datum.id}:
                                                    </strong> {datum.value} leads
                                                </div>
                                            </div>
                                        )}
                                        animate={true}
                                        motionConfig="gentle"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default MultiLineMetricsChart;
